import React, { useRef, useState, useCallback, useEffect } from 'react'
import usePhoneControl from '../../hooks/usePhoneControl'
import styles from './PhoneMirror.module.css'

export default function PhoneMirror({ remoteStream, onDisconnect, sendSignal, phoneDimensions }) {
  const videoRef = useRef(null)
  const menuRef = useRef(null)
  const [contextMenu, setContextMenu] = useState(null)
  const { containerRef, handleClick, handleSwipe, sendBack, sendHome } = usePhoneControl({
    send: sendSignal,
  })

  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream
    }
  }, [remoteStream])

  const onClick = useCallback(
    (e) => {
      if (contextMenu) {
        setContextMenu(null)
        return
      }
      handleClick(e, phoneDimensions.width, phoneDimensions.height)
      // ripple
      const rect = containerRef.current.getBoundingClientRect()
      const ripple = document.createElement('span')
      ripple.className = styles.ripple
      ripple.style.setProperty('--ripple-x', e.clientX - rect.left + 'px')
      ripple.style.setProperty('--ripple-y', e.clientY - rect.top + 'px')
      containerRef.current.appendChild(ripple)
      setTimeout(() => ripple.remove(), 600)
    },
    [handleClick, contextMenu, phoneDimensions, containerRef]
  )

  const onContextMenu = useCallback(
    (e) => {
      e.preventDefault()
      const rect = containerRef.current.getBoundingClientRect()
      setContextMenu({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
    },
    [containerRef]
  )

  useEffect(() => {
    if (menuRef.current && contextMenu) {
      menuRef.current.style.left = contextMenu.x + 'px'
      menuRef.current.style.top = contextMenu.y + 'px'
    }
  }, [contextMenu])

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') sendBack()
      if (e.key === 'Home') sendHome()
      if (e.key === 'ArrowUp') handleSwipe('up')
      if (e.key === 'ArrowDown') handleSwipe('down')
      if (e.key === 'ArrowLeft') handleSwipe('left')
      if (e.key === 'ArrowRight') handleSwipe('right')
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [sendBack, sendHome, handleSwipe])

  const menuActions = [
    { label: 'Swipe up', action: () => handleSwipe('up') },
    { label: 'Swipe down', action: () => handleSwipe('down') },
    { label: 'Swipe left', action: () => handleSwipe('left') },
    { label: 'Swipe right', action: () => handleSwipe('right') },
    { label: 'Go back', action: sendBack },
    { label: 'Go home', action: sendHome },
  ]

  return (
    <div
      ref={containerRef}
      className={styles.wrap}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className={styles.video}
        aria-label="Phone screen mirror"
      />
      {contextMenu && (
        <ul
          ref={menuRef}
          className={styles.contextMenu}
          role="menu"
        >
          {menuActions.map((a) => (
            <li key={a.label}>
              <button
                className={styles.menuItem}
                onClick={(e) => {
                  e.stopPropagation()
                  a.action()
                  setContextMenu(null)
                }}
                role="menuitem"
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
